import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";

type MarqueeProps = {
  children: ReactNode;
  /** Seconds for one full loop. Lower = faster. */
  duration?: number;
  reverse?: boolean;
  className?: string;
  itemClassName?: string;
  pauseOnHover?: boolean;
  /** CSS length used between items, also applied as trailing space per half. */
  gap?: string;
};

/**
 * Infinite horizontal ticker. The track holds the children twice and slides by
 * exactly -50%, so the loop is seamless. Each half carries trailing space equal
 * to the item gap, which keeps the -50% translation mathematically exact.
 */
export function Marquee({
  children,
  duration = 38,
  reverse = false,
  className,
  itemClassName,
  pauseOnHover = true,
  gap = "1.5rem",
}: MarqueeProps) {
  const halfStyle: CSSProperties = { gap, paddingRight: gap };

  return (
    <div className={cn("fade-edges-x relative overflow-hidden", className)}>
      <div
        className={cn(
          "flex w-max",
          reverse ? "animate-marquee-reverse" : "animate-marquee",
          pauseOnHover && "hover:[animation-play-state:paused]",
        )}
        style={{ animationDuration: `${duration}s` }}
      >
        <div className={cn("flex shrink-0", itemClassName)} style={halfStyle}>
          {children}
        </div>
        <div className={cn("flex shrink-0", itemClassName)} style={halfStyle} aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
